import { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Label shown when nothing is selected. */
  allLabel?: string;
  /** Label shown when every option is selected. */
  everyLabel?: string;
  /**
   * Function to render the button label when at least one (but not all)
   * options are selected. Defaults to comma-joined option labels, falling back
   * to "{n} selected" when more than three are chosen.
   */
  formatSelected?: (selectedOptions: MultiSelectOption[]) => string;
  ariaLabel?: string;
  className?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  allLabel = "All",
  everyLabel,
  formatSelected,
  ariaLabel,
  className = "",
}: MultiSelectProps) {
  const selectedSet = new Set(selected);
  const selectedOptions = options.filter((o) => selectedSet.has(o.value));
  const allSelected = options.length > 0 && selectedOptions.length === options.length;
  const noneSelected = selectedOptions.length === 0;

  const buttonLabel = (() => {
    if (noneSelected) return allLabel;
    if (allSelected && everyLabel) return everyLabel;
    if (formatSelected) return formatSelected(selectedOptions);
    if (selectedOptions.length <= 3) {
      return selectedOptions.map((o) => o.label).join(", ");
    }
    return `${selectedOptions.length} selected`;
  })();

  return (
    <Listbox value={selected} onChange={onChange} multiple>
      <div className={`relative ${className}`}>
        <Listbox.Button
          aria-label={ariaLabel}
          className="relative flex items-center w-full py-2 pl-3 pr-8 text-sm text-left border border-stone-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent text-stone-700"
        >
          <span className="block truncate">{buttonLabel}</span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronUpDownIcon className="w-4 h-4 text-stone-400" aria-hidden />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-50 mt-1 w-full min-w-[10rem] overflow-auto rounded-lg bg-white border border-stone-200 shadow-lg max-h-60 py-1 text-sm focus:outline-none">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-stone-400 select-none">No options.</div>
            ) : (
              options.map((option) => (
                <Listbox.Option key={option.value} value={option.value} as={Fragment}>
                  {({ active, selected: isSelected }) => (
                    <li
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer select-none ${
                        active ? "bg-forest-50 text-forest-900" : "text-stone-800"
                      }`}
                    >
                      <CheckIcon
                        className={`w-4 h-4 flex-shrink-0 ${
                          isSelected ? "text-forest-600" : "invisible"
                        }`}
                      />
                      <span className={isSelected ? "font-medium" : ""}>{option.label}</span>
                    </li>
                  )}
                </Listbox.Option>
              ))
            )}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}
